'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Briefcase, Check, Store } from 'lucide-react'
import './home-v2.css'

const TRUST = ['GoHighLevel Certified Admins', 'White-labeled under your brand', '24/7 coverage']

const PILL_EVERY_MS = 2200
// Matches the .hv2-pill.is-leaving transition in home-v2.css.
const PILL_EXIT_MS = 240

// The rotating pill above the headline. Its state lives here, not in
// HomePage: ticking it there re-rendered the whole homepage every 2.2s.
// The old pill blurs up and out, then the next one blurs in from below
// (CSS, home-v2.css). The first pill is in the server HTML, visible at once.
function RotatingPill({ pills }) {
  const [index, setIndex] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const [rotated, setRotated] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setLeaving(true), PILL_EVERY_MS)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!leaving) return undefined
    const id = setTimeout(() => {
      setIndex((current) => (current + 1) % pills.length)
      setRotated(true)
      setLeaving(false)
    }, PILL_EXIT_MS)
    return () => clearTimeout(id)
  }, [leaving, pills.length])

  return (
    <span
      key={index}
      className={`hv2-pill${rotated ? ' is-entering' : ''}${leaving ? ' is-leaving' : ''}`}
    >
      <span className="hv2-pill-dot" aria-hidden="true" />
      {pills[index]}
    </span>
  )
}

export default function HeroV2({ rotatingPills }) {
  return (
    <section className="hv2 hv2-section is-tint hv2-hero">
      <span className="hv2-bloom one" aria-hidden="true" />
      <span className="hv2-bloom two" aria-hidden="true" />

      <div className="hv2-inner hv2-hero-grid">
        <div>
          <div className="hv2-pill-slot">
            <RotatingPill pills={rotatingPills} />
          </div>

          {/* LOCKED (T-11.1): H1 text. Do not change before 2026-12-15. */}
          {/* The headline and the paragraph under it are the page's Largest
              Contentful Paint. They used to fade in from opacity 0, so they
              only counted as painted once all the JavaScript had loaded and
              run (20 s LCP on a mobile Lighthouse run). They now render
              visible straight from the server HTML. */}
          <h1>
            Hire a Dedicated{' '}
            <span className="hv2-nowrap">
              <span className="go">Go</span><span className="high">High</span><span className="level">Level</span>
            </span>{' '}
            Expert Team
          </h1>

          {/* Answer-first: the opening sentence answers "What is GHL Prime?"
              on its own (answer engines quote the first paragraph). Short
              sentences, about 40 words in all. */}
          <p className="hv2-lede speakable-intro">
            GHL Prime is a certified GoHighLevel expert team. We set up, automate and support
            GoHighLevel for business owners and agencies, all under your brand. Hire the full
            team or one specialist for builds, workflows, AI agents and 24/7 client support.
          </p>

          {/* The buttons and trust row rise in with CSS (home-v2.css), so they
              play on the first paint instead of waiting for JavaScript. */}
          <div className="hv2-hero-cta">
            {/* Two audience doors: owners using GoHighLevel in their own
                business, and agencies reselling it under their brand. */}
            <div className="hv2-door">
              <Link href="/for-business-owners" className="primary-pill large hv2-door-btn">
                <Store size={18} aria-hidden="true" /> I Own a Business <ArrowRight size={17} aria-hidden="true" />
              </Link>
              <span className="hv2-door-note">For my own company</span>
            </div>
            <div className="hv2-door">
              <Link href="/for-agency-owners" className="secondary-pill large hv2-door-btn">
                <Briefcase size={18} aria-hidden="true" /> I Run an Agency <ArrowRight size={17} aria-hidden="true" />
              </Link>
              <span className="hv2-door-note">Resell GHL to my clients</span>
            </div>
          </div>

          <div className="hv2-hero-trust">
            {TRUST.map((t) => (
              <span className="hv2-trust-item" key={t}><Check size={15} /> {t}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
